/**
 * Token Transfer Utility
 * Handles secure transfer of authentication tokens between tenant subdomains
 */

const TOKEN_PARAM = 'auth_transfer';

/**
 * Encode auth data for URL transfer
 * @param {Object} authData - Object containing tokens and auth info
 * @returns {string} - Base64 encoded string
 */
export function encodeAuthData(authData) {
  try {
    const jsonString = JSON.stringify(authData);
    return btoa(encodeURIComponent(jsonString));
  } catch (e) {
    console.error('Failed to encode auth data:', e);
    return null;
  }
}

/**
 * Decode auth data from URL parameter
 * @param {string} encoded - Base64 encoded string
 * @returns {Object|null} - Decoded auth data or null if invalid
 */
export function decodeAuthData(encoded) {
  try {
    const jsonString = decodeURIComponent(atob(encoded));
    return JSON.parse(jsonString);
  } catch (e) {
    console.error('Failed to decode auth data:', e);
    return null;
  }
}

/**
 * Build redirect URL with auth data
 * @param {string} targetUrl - The target URL to redirect to
 * @returns {string} - URL with encoded auth data parameter
 */
export function buildTransferUrl(targetUrl) {
  const authData = {
    token: localStorage.getItem('user-token'),
    refreshToken: localStorage.getItem('refresh-token'),
    tokenTime: localStorage.getItem('user-token-time'),
    rememberMe: localStorage.getItem('remember-me'),
    loginInfo: localStorage.getItem('login-info'),
    tenant: localStorage.getItem('tenant'),
    rememberTenant: localStorage.getItem('rememberTenant')
  };

  const encoded = encodeAuthData(authData);
  if (!encoded) {
    return targetUrl;
  }

  const url = new URL(targetUrl);
  url.searchParams.set(TOKEN_PARAM, encoded);
  return url.toString();
}

/**
 * Extract and apply auth data from URL if present
 * Returns true if auth data was found and applied
 * @returns {boolean}
 */
export function extractAndApplyAuthFromUrl() {
  const urlParams = new URLSearchParams(window.location.search);
  const encoded = urlParams.get(TOKEN_PARAM);

  if (!encoded) {
    return false;
  }

  const authData = decodeAuthData(encoded);
  if (!authData || !authData.token) {
    return false;
  }

  // Apply auth data to localStorage
  if (authData.token) {
    localStorage.setItem('user-token', authData.token);
  }
  if (authData.refreshToken) {
    localStorage.setItem('refresh-token', authData.refreshToken);
  }
  if (authData.tokenTime) {
    localStorage.setItem('user-token-time', authData.tokenTime);
  }
  if (authData.rememberMe) {
    localStorage.setItem('remember-me', authData.rememberMe);
  }
  if (authData.loginInfo) {
    localStorage.setItem('login-info', authData.loginInfo);
  }
  if (authData.tenant) {
    localStorage.setItem('tenant', authData.tenant);
  }
  if (authData.rememberTenant) {
    localStorage.setItem('rememberTenant', authData.rememberTenant);
  }

  // Clean URL by removing the auth_transfer parameter
  cleanUrlParams();

  return true;
}

/**
 * Remove auth_transfer parameter from URL without page reload
 */
function cleanUrlParams() {
  const url = new URL(window.location.href);
  url.searchParams.delete(TOKEN_PARAM);

  // Use replaceState to update URL without reload
  window.history.replaceState({}, document.title, url.pathname + url.search);
}
